
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { format } from 'date-fns';

import BDHelmet from '@/components/BDHelmet';
import Header from '@/components/Header/Header';
import Footer from '@/components/Footer/Footer';
import Loader from '@/components/ui/Loader/Loader';
import Video from '@/components/Modal/General/Video';
import ProjectDetail from '@/components/Modal/Project/ProjectDetail';
import FileRow from './FileRow';

import base64ToBlob from '@/helper/base64ToBlob';
import usePatch from '@/hooks/usePatch';
import projectApi from '@/api/project';
import fileApi from '@/api/file';

const { getGlobalProduct } = projectApi();
const { getPublicAttachments } = fileApi();

const Children = () => {
	const dispatch = useDispatch();
	const showVideo = useSelector((state) => state.trigger.showVideo);
	const params = useParams();
	const { number, name, id } = params;

	const [fileSizeDownload, setFileSizeDownload] = useState(null);
	const [showPopup, setShowPopup] = useState(false);

	const {
		data: p,
		isFetching: isFetchingP,
		isFetched: isFetchedP,
	} = useQuery({
		queryKey: ['project', number, name, id],
		enabled: !!number && !!name && !!id,
		queryFn: () => getGlobalProduct(id, { public: 1 }),
	});
	const data = useMemo(() => p?.data || null, [p]) || null;

	const { data: a } = useQuery({
		queryKey: ['file', id],
		queryFn: async () => {
			try {
				const result = await getPublicAttachments(id, { public: 1 });
				return result;
			} catch (error) {
				return error;
			}
		},
		enabled: !!id, // Only run query if id exists
	});
	const docs =
		useMemo(() => {
			const res = a?.data?.attachments;
			if (typeof res === 'string') {
				return [];
			}
			const result = a?.data?.attachments || [];
			return result;
		}, [a]) || [];

	const {
		mutate: zipDownload,
		isPending: isPendingZip,
		isSuccess: isSuccessZip,
	} = usePatch({
		url: `downloadlink`,
		method: 'post',
		type: 'form-data',
		callback: async (res) => {
			try {
				const blob = base64ToBlob(res.data.zip_file, 'application/zip');

				const fileSizeMB = (blob.size / (1024 * 1024)).toFixed(2);

				setFileSizeDownload(`${fileSizeMB}MB`);
				setShowPopup(true);

				const pType = data.productprefix_detail?.length ? data.productprefix_detail[0]?.productprefix : data.productprefix || '';

				const link = document.createElement('a');
				link.href = URL.createObjectURL(blob);
				link.download = `${pType} ${data.productcodenumber} ${data.productname} ${format(new Date(), 'yyyy-MM-dd HH:mm:ss')}.zip`;
				document.body.appendChild(link);

				setTimeout(() => {
					link.click();
					document.body.removeChild(link);

					setTimeout(() => {
						setShowPopup(false);
						setFileSizeDownload(null);
					}, 3000);
				}, 100);
				// eslint-disable-next-line no-empty
			} catch (error) {}
		},
		onError: (err) => {
			toast.error(`${err.status} - ${err.statusText}`);
		},
	});

	const onZipDownload = () => {
		if (docs.length) {
			const postDownloadFormat = {
				productid: id,
				attachments: JSON.stringify(docs),
				zip_file: true,
			};
			zipDownload(postDownloadFormat);
		}
	};

	const project = data ? `${data.productprefix} ${data.productcodenumber} ${data.productname}` : '';
	return (
		<div className='flex flex-1 h-full bg-bd-bg'>
			<div className='container flex flex-col w-full h-full gap-4 pt-8 pb-8 mx-auto focus-visible:outline-none'>
				{((isFetchingP && !isFetchedP) || (isPendingZip && !isSuccessZip)) && <Loader />}
				{fileSizeDownload && showPopup && (
					<div className='absolute z-50 h-full -translate-x-1/2 top-1/2 left-1/2'>
						<span className=' animate-pulse'>Downloading {fileSizeDownload} file</span>
					</div>
				)}

				<h1 className='px-4 text-2xl font-medium sm:px-0 text-wrap'> {project}</h1>
				<div className='flex flex-col gap-4'>
					<button className='w-auto btn ml-auto' onClick={onZipDownload} disabled={!docs.length}>
						Zip Download
					</button>
					<div className='grid w-full grid-cols-12 text-sm text-gray-500'>
						<div className='col-span-10 text-center'>Navn</div>
						<div className='col-span-2 text-center'>link</div>
					</div>
					<div>
						<div id='relations' className='w-full'>
							{data && <FileRow project={data} idx={0} />}
						</div>
					</div>
				</div>
				<span className='px-4 font-bold sm:px-10 text-wrap'>
					For iphone/safari brukere: gå til innstillenger/Safari/blokker pop-up, sett til av
				</span>
			</div>
			{showVideo && (
				<Video
					open={!!showVideo}
					video={showVideo}
					onClose={() => dispatch({ type: 'trigger/triggerAPI', payload: { key: 'showVideo', value: null } })}
				/>
			)}
		</div>
	);
};

export default function File() {
	const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
	const showPublicDetail = useSelector((state) => state.trigger.showPublicDetail);
	const params = useParams();
	const title = `File | ${params.number} ${params.name}`;
	return (
		<>
			<BDHelmet title={title} description={title} />
			{isAuthenticated ? (
				<Children />
			) : (
				<div className='layout'>
					<Header />
					<Children />
					<Footer />
				</div>
			)}
			{!!showPublicDetail && <ProjectDetail open={!!showPublicDetail} />}
		</>
	);
}
